import React, { useEffect, useState } from 'react';

import * as reportsService from '../../services/api/reports';
import { useAuth } from '../../hooks/use-auth';
import { strings } from '../../localization/strings';
import { PressreleaseStatsReport } from '../../entities/pressrelease-stats-report';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import CommonAnimatedLoader from '../../components/common/animated/loader';

function PressreleaseStatsReportPageComponent() {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [report, setReport] = useState<PressreleaseStatsReport>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await reportsService.getPressreleaseStatsReport(token);

        if (result) {
          setReport(result);
        }
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [token]);

  const exportData = async () => {
    const blob = await reportsService.exportPressreleaseStatsReport(token);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = downloadUrl;
    link.download = `pressrelease-stats-${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const total = report ? report.rows.reduce((acc, row) => acc + row.count, 0) : 0;

  return (
    <>
      <header className="mb-3">
        <div className="flex space-x-3 justify-between items-center">
          <h1 className="text-3xl font-bold leading-tight text-gray-900">
            {strings.navigation.reports.pressreleaseStats}
          </h1>
          <button
            type="button"
            onClick={exportData}
            disabled={isLoading || !report}
            className="relative inline-flex items-center justify-center gap-x-1.5 rounded-md px-1 py-1 text-sm font-semibold text-white bg-[#3574d1] hover:bg-[#2c5fb0] focus:ring-2 focus:ring-offset-2 focus:ring-[#3574d1] max-h-[40px] max-w-[100px] disabled:opacity-50"
          >
            EXPORT
          </button>
        </div>
      </header>
      <main className="w-full">
        {error && (
          <div className="mx-auto my-5">
            <AlertPrimary type="danger" title={error} />
          </div>
        )}

        {isLoading && <CommonAnimatedLoader />}

        {!isLoading && !!report && report.rows.length > 0 ? (
          <div className="mt-8 flow-root border-top-1 border-top-gray-200">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.pressreleaseStats.year}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.pressreleaseStats.count}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.rows.map((row) => (
                      <tr key={`pressrelease-stats-${row.year}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{row.year}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{row.count}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {strings.pressreleaseStats.total}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">{total}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          !error &&
          !isLoading && (
            <div className="my-5">
              <AlertPrimary type="failed" text={strings.search.noResults} />
            </div>
          )
        )}
      </main>
    </>
  );
}

export default PressreleaseStatsReportPageComponent;
